import React from 'react';
import cx from 'classnames';

import { Pressable, PressableProps } from '../pressable';

export type TextButtonTone = 'ink' | 'primary' | 'muted' | 'report';
export type TextButtonSize = 'sm' | 'md' | 'lg';

export interface TextButtonProps extends PressableProps {
  /** `primary` is a red action under a form («Запросить код повторно»); `muted` is a grey one beside a form («Сохранить черновик»); `report` sits in the journal's AI reports. */
  tone?: TextButtonTone;
  /** The label type of a `Button` of the same size, for an action standing next to buttons. Without it the text takes the type of `className` or of the sentence. */
  size?: TextButtonSize;
  underline?: 'always' | 'hover' | 'none';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const toneClasses: Record<TextButtonTone, string> = {
  ink: 'text-ink',
  primary: 'text-primary active:text-primary-active disabled:opacity-50',
  muted: 'text-ink-muted hover:text-ink disabled:text-ink-subtle',
  report: 'text-report hover:text-report-hover',
};

const sizeClasses: Record<TextButtonSize, string> = {
  sm: 'text-label-sm',
  md: 'text-label',
  lg: 'text-label-lg',
};

const underlineClasses = {
  always: 'underline disabled:no-underline',
  hover: 'enabled:hover:underline',
  none: '',
};

/**
 * An action drawn as text — «Редактировать» beside a block title, «Печать» in a report header. It has no
 * box and takes the size of its `className` or of the sentence around it. A link that goes somewhere is
 * `TextLink`; an action that looks like a button is `Button`.
 */
export const TextButton = ({
  tone = 'ink',
  size,
  underline = 'hover',
  startIcon,
  endIcon,
  className,
  children,
  ...rest
}: TextButtonProps) => (
  <Pressable
    className={cx(
      'inline-flex items-center gap-1.5 rounded-chip transition-colors',
      toneClasses[tone],
      size && sizeClasses[size],
      underlineClasses[underline],
      className,
    )}
    {...rest}
  >
    {startIcon}
    {children}
    {endIcon}
  </Pressable>
);
